import React, { useState, useEffect, useRef, useCallback } from 'react';

import img1 from '../assets/customerfeedbackimages/1.jpeg';
import img2 from '../assets/customerfeedbackimages/2.jpeg';
import img3 from '../assets/customerfeedbackimages/3.jpeg';
import img4 from '../assets/customerfeedbackimages/4.jpeg';
import img5 from '../assets/customerfeedbackimages/5.jpeg';
import img6 from '../assets/customerfeedbackimages/6.jpeg';
import img7 from '../assets/customerfeedbackimages/7.jpeg';
import img8 from '../assets/customerfeedbackimages/8.jpeg';
import img9 from '../assets/customerfeedbackimages/9.jpeg';
import img10 from '../assets/customerfeedbackimages/10.jpeg';
import img11 from '../assets/customerfeedbackimages/11.jpeg';
import img12 from '../assets/customerfeedbackimages/12.jpeg';
import img13 from '../assets/customerfeedbackimages/13.jpeg';
import img14 from '../assets/customerfeedbackimages/14.jpeg';
import img15 from '../assets/customerfeedbackimages/15.jpeg';
import img16 from '../assets/customerfeedbackimages/16.jpeg';
import img17 from '../assets/customerfeedbackimages/17.jpeg';
import img18 from '../assets/customerfeedbackimages/18.jpeg';
import imgWA1 from '../assets/customerfeedbackimages/WhatsApp Image 2026-04-02 at 4.50.34 PM.jpeg';
import imgWA2 from '../assets/customerfeedbackimages/WhatsApp Image 2026-04-02 at 4.50.34 PM (1).jpeg';
import imgWA3 from '../assets/customerfeedbackimages/WhatsApp Image 2026-04-02 at 4.50.35 PM.jpeg';

const images = [
  img1, img2, img3, img4, img5, img6, img7, img8, img9, img10,
  img11, img12, img13, img14, img15, img16, img17, img18,
  imgWA1, imgWA2, imgWA3,
];

const VISIBLE_CARDS = 3; // Number of stacked cards visible behind the front card

const CustomerFeedbackSlider: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('left');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => {
    if (isAnimating) return;
    setDirection('left');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setIsAnimating(false);
    }, 400);
  }, [isAnimating]);

  const prev = useCallback(() => {
    if (isAnimating) return;
    setDirection('right');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      setIsAnimating(false);
    }, 400);
  }, [isAnimating]);

  const resetAutoPlay = useCallback(() => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(next, 3000);
  }, [next]);

  useEffect(() => {
    autoPlayRef.current = setInterval(next, 3000);
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [next]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) { next(); } else { prev(); }
      resetAutoPlay();
    }
    touchStartX.current = null;
  };

  const getCardStyle = (offset: number): React.CSSProperties => {
    // offset 0 = front card, 1 = behind, 2 = further behind, etc.
    const scale = 1 - offset * 0.06;
    const translateY = offset * -8;
    const translateX = offset * 6;
    const rotate = offset * -2;
    const opacity = offset === 0 ? 1 : 1 - offset * 0.15;
    const zIndex = VISIBLE_CARDS - offset;

    return {
      transform: `translateX(${translateX}px) translateY(${translateY}px) scale(${scale}) rotate(${rotate}deg)`,
      opacity,
      zIndex,
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    };
  };

  const getFrontCardAnimation = (): React.CSSProperties => {
    if (!isAnimating) return {};
    const xMove = direction === 'left' ? -350 : 350;
    const rotate = direction === 'left' ? -15 : 15;
    return {
      transform: `translateX(${xMove}px) rotate(${rotate}deg)`,
      opacity: 0,
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    };
  };

  const getVisibleCards = () => {
    const cards = [];
    for (let i = 0; i <= VISIBLE_CARDS; i++) {
      const imgIndex = (currentIndex + i) % images.length;
      cards.push({ imgIndex, offset: i });
    }
    return cards;
  };

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-1 h-5 bg-minto-500 rounded-full" />
          <h2 className="text-base md:text-lg font-semibold text-gray-800">Happy Customers</h2>
        </div>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
          {currentIndex + 1} / {images.length}
        </span>
      </div>

      {/* Card Stack */}
      <div
        className="relative flex items-center justify-center"
        style={{ height: '340px', perspective: '1000px' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Stacked Cards */}
        <div className="relative" style={{ width: '220px', height: '310px' }}>
          {getVisibleCards().reverse().map(({ imgIndex, offset }) => (
            <div
              key={`${currentIndex}-${offset}`}
              className="absolute inset-0 rounded-2xl overflow-hidden shadow-xl cursor-pointer"
              style={{
                ...getCardStyle(offset),
                ...(offset === 0 ? getFrontCardAnimation() : {}),
              }}
              onClick={() => {
                if (offset === 0) setSelectedIndex(imgIndex);
              }}
            >
              <img
                src={images[imgIndex]}
                alt={`Happy customer ${imgIndex + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Gradient overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          ))}
        </div>

        {/* Left Arrow */}
        {/* Right Arrow */}
      </div>

      {/* Lightbox */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setSelectedIndex(null)}
        >
          <div className="relative max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <img
              src={images[selectedIndex]}
              alt={`Customer feedback ${selectedIndex + 1}`}
              className="w-full rounded-2xl shadow-2xl"
            />
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute -top-3 -right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg text-gray-600 hover:text-red-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              onClick={() => setSelectedIndex((selectedIndex - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setSelectedIndex((selectedIndex + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerFeedbackSlider;
